// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a meeting game kept as a Khipu (`docs/AYNI_XR_MEETING_GAMES_PLAN.md`
 * §4.3, decision G5): a story with one step per round played, built on the
 * presenter's page from the rounds it sent and the results the room showed.
 *
 * Each step puts the round's dataset and, for a place, the right place on
 * the globe, and its captions say the question and what the room answered
 * in totals (so many for each option, so many pins). A trivia round keeps
 * its quiz, so the Khipu can be played as trivia again. Never a name, a
 * score or a board: nothing of who answered what.
 *
 * Pure.
 */

import type { Khipu, KhipuDatasetRef, KhipuStep } from './khipu'
import { KHIPU_SCHEMA_VERSION } from './khipu'
import type { RoundResult, RoundSpec } from './roomGame'

/** One round as the presenter's page saw it: what it sent, on which dataset, and the results shown. */
export interface GameLogRound {
  /** The room's id of the round, so a round is logged once. */
  id?: string
  spec: RoundSpec
  datasetId: string | null
  result: RoundResult | null
}

export interface GameKhipuOptions {
  id: string
  title: string
  lang: string
  now: number
  titleOf?: (datasetId: string) => string | null
  /** The words of the captions, in the presenter's language. */
  words: {
    round: (n: number) => string
    counts: (list: string) => string
    place: (label: string, pins: number) => string
    pins: (n: number) => string
  }
}

const LETTERS = 'ABCDEF'

/** The rounds played, as a draft story Khipu (one step a round). */
export function buildGameKhipu(rounds: readonly GameLogRound[], opts: GameKhipuOptions): Khipu {
  const L = (text: string): Record<string, string> => ({ [opts.lang]: text })
  const datasets: KhipuDatasetRef[] = []
  const keys = new Map<string, string>()
  const keyOf = (id: string): string => {
    let key = keys.get(id)
    if (!key) {
      key = `d${keys.size + 1}`
      keys.set(id, key)
      datasets.push({ key, ref: `app:${id}`, appId: id, title: { en: opts.titleOf?.(id) ?? id } })
    }
    return key
  }
  const steps: KhipuStep[] = rounds.map((r, i) => {
    const n = i + 1
    const captions: string[] = [r.spec.prompt]
    const step: KhipuStep = { id: `s${n}`, n, title: L(opts.words.round(n)), dataset: r.datasetId ? keyOf(r.datasetId) : null, advance: { mode: 'manual' } }
    if (r.spec.kind === 'choice') {
      const counts = r.result?.counts
      if (counts) captions.push(opts.words.counts(r.spec.options.map((_, j) => `${LETTERS[j]} ${counts[j] ?? 0}`).join(' · ')))
      // The quiz again, for playing it as trivia another day (four answers at most, as a Khipu's quiz).
      if (r.spec.options.length <= 4) {
        step.quiz = { question: L(r.spec.prompt), options: r.spec.options.map((o) => L(o)), correctIndex: r.spec.correct, difficulty: r.spec.difficulty }
      }
    } else {
      const pins = r.result?.pins?.length ?? 0
      if (r.spec.answer) {
        step.view = { lat: r.spec.answer[0], lon: r.spec.answer[1], zoom: 3 }
        step.pins = [{ lat: r.spec.answer[0], lon: r.spec.answer[1], color: 0, ...(r.spec.label ? { note: L(r.spec.label) } : {}) }]
        captions.push(opts.words.place(r.spec.label, pins))
      } else {
        captions.push(opts.words.pins(pins))
      }
      if (r.spec.fact) captions.push(r.spec.fact)
    }
    step.prompts = captions.map((text) => ({ kind: 'caption', audience: 'everyone', text: L(text) }))
    return step
  })
  if (steps.length === 0) steps.push({ id: 's1', n: 1, dataset: null })
  const iso = new Date(opts.now).toISOString()
  return {
    format: 'ayni-khipu',
    schemaVersion: KHIPU_SCHEMA_VERSION,
    id: opts.id,
    kind: 'story',
    status: 'draft',
    title: L(opts.title),
    languages: [opts.lang],
    created: iso,
    modified: iso,
    privacy: { mode: 'anonymous' },
    datasets,
    path: { steps },
  }
}
